import {PaletteMode} from "@mui/material";
import {ThemeOptions} from "@mui/material/styles";
import {alpha} from "@mui/material";
import {
  getSemanticColorTokens,
  grey,
  type SemanticColorTokens,
} from "./colors/aptosColorPalette";
import React from "react";

const instrumentSansFontFamily =
  '"Instrument Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

// Button variants
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    nav: true;
    primary: true;
  }
}

// Divider variant - dotted
declare module "@mui/material/Divider" {
  interface DividerPropsVariantOverrides {
    dotted: true;
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    stats: React.CSSProperties;
  }
  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    stats?: React.CSSProperties;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    tokens: SemanticColorTokens;
    lineShade: {
      main: string;
    };
    neutralShade: {
      main: string;
      lighter: string;
      darker: string;
    };
  }
  interface PaletteOptions {
    tokens?: SemanticColorTokens;
    lineShade?: {
      main: string;
    };
    neutralShade?: {
      main: string;
      lighter: string;
      darker: string;
    };
  }
}

// Divider variant - big stats
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    stats: true;
  }
}

// Divider variant - bump
declare module "@mui/material/Divider" {
  interface DividerPropsVariantOverrides {
    bump: true;
    bumpDark: true;
    bumpRight: true;
    bumpRightDark: true;
  }
}

const buildDesignTokens = (
  mode: PaletteMode,
  tokens: SemanticColorTokens,
): ThemeOptions => ({
  shape: {
    borderRadius: 12,
  },
  //

  typography: {
    fontFamily: instrumentSansFontFamily,
    fontWeightLight: 200,
    fontWeightRegular: 400,
    fontWeightBold: 500,
    h1: {
      fontFamily: instrumentSansFontFamily,
      fontWeight: "600",
    },
    h2: {
      fontFamily: instrumentSansFontFamily,
      fontWeight: "600",
    },
    h3: {
      fontFamily: instrumentSansFontFamily,
      fontWeight: "600",
    },
    h4: {
      fontFamily: instrumentSansFontFamily,
      fontWeight: "600",
    },
    h5: {
      fontFamily: instrumentSansFontFamily,
      fontWeight: "600",
    },
    h6: {
      fontFamily: instrumentSansFontFamily,
      fontWeight: "600",
    },
    stats: {
      fontFamily: instrumentSansFontFamily,
      fontWeight: "400",
    },
    subtitle1: {
      fontWeight: 400,
      textTransform: "uppercase",
      lineHeight: "1.25",
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: "1rem",
      textTransform: "capitalize",
      lineHeight: "1.25",
    },
  },

  palette: {
    mode,
    tokens,
    ...(mode === "light"
      ? {
          // light mode palette values
          primary: {
            main: tokens.function.primary,
            dark: tokens.function.primaryClick,
            light: tokens.link.normal,
            contrastText: tokens.static.white,
          },

          secondary: {
            main: tokens.text.secondary,
          },

          success: {
            main: tokens.function.green,
            dark: tokens.function.greenClick,
          },

          error: {
            main: tokens.function.red,
            dark: tokens.function.redClick,
          },

          warning: {
            main: tokens.function.orange,
            dark: tokens.function.orangeClick,
          },

          background: {
            default: tokens.background.primary,
            paper: tokens.layer.card,
          },

          text: {
            primary: tokens.text.primary,
            secondary: tokens.text.secondary,
            disabled: tokens.text.disabled,
          },

          divider: tokens.line.gutter,

          lineShade: {
            main: tokens.line.gutter,
          },

          neutralShade: {
            main: tokens.background.tertiary,
            lighter: tokens.background.secondary,
            darker: tokens.layer.dropdown,
          },
        }
      : {
          // dark mode palette values
          primary: {
            main: tokens.function.primary,
            dark: tokens.function.primaryClick,
            light: tokens.link.normal,
            contrastText: tokens.static.white,
          },

          secondary: {
            main: tokens.text.secondary,
          },

          success: {
            main: tokens.function.green,
            dark: tokens.function.greenClick,
          },

          error: {
            main: tokens.function.red,
            dark: tokens.function.redClick,
          },

          warning: {
            main: tokens.function.orange,
            dark: tokens.function.orangeClick,
          },

          background: {
            default: tokens.background.primary,
            paper: tokens.layer.card,
          },

          text: {
            primary: tokens.text.primary,
            secondary: tokens.text.secondary,
            disabled: tokens.text.disabled,
          },

          divider: tokens.line.gutter,

          lineShade: {
            main: tokens.line.gutter,
          },

          neutralShade: {
            main: tokens.background.tertiary,
            lighter: tokens.background.secondary,
            darker: tokens.layer.dropdown,
          },
        }),
  },

  components: {
    // Typography overrides
    MuiTypography: {
      styleOverrides: {
        subtitle2: {
          display: "block",
        },
      },
    },
    // Autocomplete overrides
    MuiAutocomplete: {
      styleOverrides: {
        root: ({theme}) => ({
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: theme.palette.tokens.layer.card,
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.tokens.line.input,
            },
          },
          "& .MuiInputLabel-root": {
            color: theme.palette.tokens.text.tertiary,
            "&.Mui-focused": {
              color: theme.palette.tokens.function.primary,
            },
          },
        }),
        paper: ({theme}) => ({
          borderRadius: "8px",
          border: `1px solid ${theme.palette.tokens.line.subtle}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.4)"
              : "0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)",
          backgroundColor: theme.palette.tokens.layer.modal,
          marginTop: "4px",
        }),
        listbox: ({theme}) => ({
          padding: "4px",
          "& .MuiAutocomplete-option": {
            minHeight: 36,
            borderRadius: "6px",
            padding: "6px 12px",
            fontSize: 14,
            lineHeight: 1.4,
            color: theme.palette.tokens.text.primary,
            transition: "background-color 0.15s ease, color 0.15s ease",
            "&:hover, &.Mui-focused": {
              backgroundColor: theme.palette.tokens.background.secondary,
            },
            "&[aria-selected='true'], &[aria-selected='true'].Mui-focused": {
              backgroundColor: theme.palette.tokens.layer.brandActive,
              color: theme.palette.tokens.function.primary,
            },
          },
          "& .MuiAutocomplete-groupLabel": {
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.4,
            color: theme.palette.tokens.text.tertiary,
            backgroundColor: theme.palette.tokens.layer.modal,
            padding: "8px 12px 4px",
            top: -4,
          },
          "& .MuiAutocomplete-groupUl": {
            padding: "0 0 4px",
            "& .MuiAutocomplete-option": {
              paddingLeft: "16px",
            },
          },
        }),
      },
    },

    // Link overrides
    MuiLink: {
      styleOverrides: {
        root: ({theme}) => ({
          fontWeight: "400",
          "&:hover": {
            color: theme.palette.tokens.link.click,
          },
        }),
      },
    },

    // Paper overrides
    MuiPaper: {
      styleOverrides: {
        root: ({theme}) => ({
          backgroundImage: "none",
          borderRadius: theme.shape.borderRadius,
          transition: "none !important",
          boxShadow: "none",
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: () => ({
          borderRadius: 0,
          transition: `transform 500ms !important`,
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        }),
        root: ({theme}) => ({
          zIndex: theme.zIndex.drawer,
        }),
      },
    },
    MuiModal: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        paper: ({theme}) => ({
          borderRadius: "8px",
          border: `1px solid ${theme.palette.tokens.line.subtle}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.4)"
              : "0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)",
          backgroundColor: theme.palette.tokens.layer.modal,
        }),
      },
    },
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        paper: ({theme}) => ({
          borderRadius: "8px",
          border: `1px solid ${theme.palette.tokens.line.subtle}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.4)"
              : "0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)",
          backgroundColor: theme.palette.tokens.layer.modal,
        }),
        list: {
          padding: "4px",
        },
      },
    },

    MuiInput: {
      styleOverrides: {
        root: () => ({
          borderRadius: 2,
        }),
      },
    },

    MuiFilledInput: {
      styleOverrides: {
        root: ({theme}) => ({
          borderRadius: `${theme.shape.borderRadius}px`,
          backgroundColor: `${
            theme.palette.mode === "dark" ? grey[700] : grey[100]
          }`,
          transition: "none",
          "&.Mui-focused": {
            filter: `${
              theme.palette.mode === "dark"
                ? "brightness(1.1)"
                : "brightness(0.98)"
            }`,
            boxShadow: `0 0 0 3px ${alpha(tokens.function.primary, 0.5)}`,
          },
          "&:hover": {
            backgroundColor: `${
              theme.palette.mode === "dark" ? grey[800] : grey[50]
            }`,
            filter: `${
              theme.palette.mode === "dark"
                ? "brightness(1.1)"
                : "brightness(0.99)"
            }`,
          },
        }),
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: ({theme}) => ({
          borderRadius: "8px",
          backgroundColor: theme.palette.tokens.layer.card,
          "&.Mui-focused": {
            boxShadow: `0 0 0 2px ${alpha(tokens.line.input, 0.2)}`,
          },
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tokens.line.subtle,
            transition: "border-color 0.15s ease",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tokens.line.strong,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tokens.line.input,
            borderWidth: "1px",
          },
        }),
      },
    },

    // Select overrides
    MuiSelect: {
      styleOverrides: {
        select: {
          borderRadius: "8px",
        },
        outlined: {
          backgroundColor: "transparent",
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({theme}) => ({
          borderRadius: "6px",
          fontSize: "14px",
          lineHeight: "1.4",
          color: theme.palette.tokens.text.primary,
          transition: "background-color 0.15s ease, color 0.15s ease",
          "&:hover": {
            backgroundColor: theme.palette.tokens.background.secondary,
          },
          "&.Mui-selected": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.tokens.layer.brandActive
                : "#EDF1FF",
            color: theme.palette.tokens.function.primary,
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? theme.palette.tokens.layer.brandActive
                  : "#EDF1FF",
            },
          },
        }),
      },
    },

    // Divider overrides
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: tokens.line.subtle,
        },
      },
      variants: [
        {
          props: {variant: "dotted"},
          style: {
            borderStyle: "dotted",
            borderWidth: "0 0 2px",
          },
        },
        {
          props: {variant: "bump"},
          style: {
            transform: "translateY(-20px)",
            border: `0`,
            height: "20px",
            background: "transparent",
            position: "relative",
            "&::before, &::after": {
              content: '""',
              position: "absolute",
              bottom: "0",
              willChange: "transform",
            },
            "&::before": {
              display: "block",
              width: "100%",
              height: "1px",
              background: tokens.function.primary,
              maskClip: "content-box",
              maskImage: `-webkit-linear-gradient(black, black),
            url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="6" viewBox="0 0 14 6"><rect fill="black" x="0" y="0" width="13" height="6"></rect></svg>')`,
              maskPosition: `0 0, 20%`,
              maskRepeat: "no-repeat",
              maskComposite: "exclude",
              WebkitMaskComposite: "xor",
            },
            "&::after": {
              width: "14px",
              height: "6px",
              backgroundSize: "100%",
              backgroundRepeat: "none",
              backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="6" viewBox="0 0 14 6"><path d="M0,5.5a2.09,2.09,0,0,0,1.51-.64L4.66,1.53a3.36,3.36,0,0,1,4.69-.15,2.28,2.28,0,0,1,.22.22l2.88,3.21A2.08,2.08,0,0,0,14,5.5" fill="none" stroke="%23${tokens.function.primary.substring(
                1,
              )}" stroke-miterlimit="10"/></svg>')`,
              transform: `translateX(calc(-1 * 20%))`,
              left: "20%",
            },
          },
        },
        {
          props: {variant: "bumpDark"},
          style: {
            transform: "translateY(-20px)",
            border: `0`,
            height: "20px",
            background: "transparent",
            position: "relative",
            "&::before, &::after": {
              content: '""',
              position: "absolute",
              bottom: "0",
              willChange: "transform",
            },
            "&::before": {
              display: "block",
              width: "100%",
              height: "1px",
              background: tokens.function.primary,
              maskClip: "content-box",
              maskImage: `-webkit-linear-gradient(black, black),
            url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="6" viewBox="0 0 14 6"><rect fill="white" x="0" y="0" width="13" height="6"></rect></svg>')`,
              maskPosition: `0 0, 20%`,
              maskRepeat: "no-repeat",
              maskComposite: "exclude",
              WebkitMaskComposite: "xor",
            },
            "&::after": {
              width: "14px",
              height: "6px",
              backgroundSize: "100%",
              backgroundRepeat: "none",
              backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="6" viewBox="0 0 14 6"><path d="M0,5.5a2.09,2.09,0,0,0,1.51-.64L4.66,1.53a3.36,3.36,0,0,1,4.69-.15,2.28,2.28,0,0,1,.22.22l2.88,3.21A2.08,2.08,0,0,0,14,5.5" fill="none" stroke="%23${tokens.function.primary.substring(
                1,
              )}" stroke-miterlimit="10"/></svg>')`,
              transform: `translateX(calc(-1 * 20%))`,
              left: "20%",
            },
          },
        },
        {
          props: {variant: "bumpRight"},
          style: {
            marginTop: "-20px",
            border: `0`,
            height: "20px",
            background: "transparent",
            marginBottom: "4rem",
            position: "relative",
            "&::before, &::after": {
              content: '""',
              position: "absolute",
              bottom: "0",
              willChange: "transform",
            },
            "&::before": {
              display: "block",
              width: "100%",
              height: "1px",
              background: "black",
              maskClip: "content-box",
              maskImage: `-webkit-linear-gradient(black, black),
            url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="6" viewBox="0 0 14 6"><rect fill="black" x="0" y="0" width="13" height="6"></rect></svg>')`,
              maskPosition: `0 0, 78%`,
              maskRepeat: "no-repeat",
              maskComposite: "exclude",
              WebkitMaskComposite: "xor",
            },
            "&::after": {
              width: "14px",
              height: "6px",
              backgroundSize: "100%",
              backgroundRepeat: "none",
              backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="6" viewBox="0 0 14 6"><path d="M0,5.5a2.09,2.09,0,0,0,1.51-.64L4.66,1.53a3.36,3.36,0,0,1,4.69-.15,2.28,2.28,0,0,1,.22.22l2.88,3.21A2.08,2.08,0,0,0,14,5.5" fill="none" stroke="black" stroke-miterlimit="10"/></svg>')`,
              transform: `translateX(calc(-1 * 78%))`,
              left: "78%",
            },
          },
        },
        {
          props: {variant: "bumpRightDark"},
          style: {
            transform: "translateY(-20px)",
            border: `0`,
            height: "20px",
            background: "transparent",
            position: "relative",
            "&::before, &::after": {
              content: '""',
              position: "absolute",
              bottom: "0",
              willChange: "transform",
            },
            "&::before": {
              display: "block",
              width: "100%",
              height: "1px",
              background: tokens.function.primary,
              maskClip: "content-box",
              maskImage: `-webkit-linear-gradient(black, black),
            url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="6" viewBox="0 0 14 6"><rect fill="white" x="0" y="0" width="13" height="6"></rect></svg>')`,
              maskPosition: `0 0, 78%`,
              maskRepeat: "no-repeat",
              maskComposite: "exclude",
              WebkitMaskComposite: "xor",
            },
            "&::after": {
              width: "14px",
              height: "6px",
              backgroundSize: "100%",
              backgroundRepeat: "none",
              backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="6" viewBox="0 0 14 6"><path d="M0,5.5a2.09,2.09,0,0,0,1.51-.64L4.66,1.53a3.36,3.36,0,0,1,4.69-.15,2.28,2.28,0,0,1,.22.22l2.88,3.21A2.08,2.08,0,0,0,14,5.5" fill="none" stroke="%23${tokens.function.primary.substring(1)}" stroke-miterlimit="10"/></svg>')`,
              transform: `translateX(calc(-1 * 78%))`,
              left: "78%",
            },
          },
        },
      ],
    },

    // Table overrides
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: "separate",
          borderSpacing: "0px 0.5rem",
        },
      },
    },

    // Table Head overrides
    MuiTableHead: {
      styleOverrides: {
        root: {
          borderBottomWidth: "0",
          background: "transparent",
          borderSpacing: "0px",
        },
      },
    },

    // Table Body overrides
    MuiTableBody: {
      styleOverrides: {
        root: {
          position: "relative",
          "&::before": {
            content: '""',
            display: "block",
            height: 10,
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        head: {
          background: "transparent",
        },
      },
    },

    // Table Cell overrides
    MuiTableCell: {
      styleOverrides: {
        head: {
          border: "0",
          background: "transparent",
          paddingBottom: "0",
        },
        root: {
          padding: "0.75rem 1.5rem 0.75rem 1.5rem",
          whiteSpace: "nowrap",
          borderStyle: "solid",
          borderWidth: "0 0 0 0",
          borderColor: grey[700],
          "&:first-of-type": {
            borderRadius: 0,
          },
          "&:last-of-type": {
            borderRadius: 0,
          },
        },
      },
    },

    // Button overrides
    MuiButtonBase: {
      defaultProps: {
        // disableRipple: true, // Re-enabled
      },
    },
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        body: {
          background: theme.palette.tokens.background.primary,
          backgroundAttachment: "fixed",
        },
        "::-webkit-scrollbar": {
          width: "9px",
          height: "9px",
        },
        "::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "::-webkit-scrollbar-thumb": {
          background: theme.palette.tokens.line.strong,
          backgroundClip: "padding-box",
          border: "3px solid transparent",
          borderRadius: "999px",
        },
        "::-webkit-scrollbar-corner": {
          background: "transparent",
        },
      }),
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableFocusRipple: true,
        disableRipple: true,
      },
      styleOverrides: {
        root: ({theme}) => ({
          transition: "none !important",
          fontWeight: "400",
          "&:hover": {
            filter: "brightness(0.98)",
          },
          "&.Mui-disabled": {
            opacity: 0.5,
            color: theme.palette.mode === "dark" ? grey[400] : "black",
          },
        }),
      },
      variants: [
        {
          props: {variant: "primary"},
          style: ({theme}) => ({
            backgroundColor: theme.palette.tokens.function.primary,
            color: theme.palette.primary.contrastText,
            fontSize: "1.1rem",
            padding: "12px 34px",
            minWidth: "8rem",
            "&:hover": {
              backgroundColor: theme.palette.tokens.function.primaryClick,
            },
          }),
        },
        {
          props: {variant: "nav"},
          style: {
            textTransform: "capitalize",
            color: grey[300],
            fontSize: "1rem",
            fontWeight: "normal",
            "&:hover": {
              background: "transparent",
              opacity: "0.8",
            },
            "&.active": {},
          },
        },
      ],
    },
  },
});

const getDesignTokens = (mode: PaletteMode): ThemeOptions =>
  buildDesignTokens(mode, getSemanticColorTokens(mode));

export default getDesignTokens;
